import { useState } from "react";
import {
  Badge,
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  Select,
  Spinner,
  Switch,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { Add24Regular, Delete20Regular, Edit20Regular } from "@fluentui/react-icons";
import { PLICHI, type Plico } from "@app/schema";
import { useData } from "../data/DataProvider";
import { eliminaVoceModello, loadModello, ordineInCoda, salvaVoceModello, type VoceModello } from "../data/checklist";
import { useAsync } from "../lib/useAsync";
import { goBack } from "../lib/nav";
import { Shell } from "../components/Shell";
import { Section } from "../components/Section";
import { ErrorState, describeError } from "../components/ErrorState";

const useStyles = makeStyles({
  list: { display: "flex", flexDirection: "column" },
  row: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalM,
    padding: `${tokens.spacingVerticalXS} 0`,
    borderTop: `1px solid ${tokens.colorNeutralStroke3}`,
  },
  body: { flexGrow: 1, minWidth: 0 },
  off: { color: tokens.colorNeutralForeground3 },
  badges: { display: "flex", gap: tokens.spacingHorizontalXS, flexWrap: "wrap" },
  meta: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  form: { display: "flex", flexDirection: "column", gap: tokens.spacingVerticalM },
  two: { display: "grid", gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1fr)", gap: tokens.spacingHorizontalM, "& > *": { minWidth: 0 } },
  full: { width: "100%", minWidth: 0 },
});

/** Documenti che si chiedono di solito (solo gestori): da qui si preparano le checklist. */
export function ModelloChecklist() {
  const s = useStyles();
  const data = useData();
  const modello = useAsync(() => loadModello(data), [data]);
  const [edit, setEdit] = useState<{ voce?: VoceModello }>();
  const [err, setErr] = useState<string>();

  async function elimina(m: VoceModello) {
    if (!window.confirm(`Togliere "${m.fields.Title}" dal modello? Le checklist già preparate non cambiano.`)) return;
    try {
      await eliminaVoceModello(data, m);
    } catch (e) {
      setErr(describeError(e).detail);
    }
    await modello.reload();
  }

  return (
    <Shell
      title="Modello checklist"
      onBack={() => goBack()}
      actions={
        <Button appearance="primary" icon={<Add24Regular />} onClick={() => setEdit({})}>
          Documento
        </Button>
      }
    >
      <span className={s.meta}>
        Documenti proposti quando si prepara la checklist di una gara o di un lotto. Le modifiche valgono per le checklist preparate da ora in poi.
      </span>
      {err && (
        <MessageBar intent="error">
          <MessageBarBody>{err}</MessageBarBody>
        </MessageBar>
      )}
      {modello.error ? (
        <ErrorState error={modello.error} />
      ) : !modello.data ? (
        <Spinner style={{ marginTop: 48 }} />
      ) : (
        PLICHI.map((p) => {
          const voci = modello.data!.filter((m) => (m.fields.Plico ?? "Documentazione Amministrativa") === p);
          return (
            <Section key={p} title={p}>
              {voci.length === 0 ? (
                <span className={s.meta}>Nessun documento.</span>
              ) : (
                <div className={s.list}>
                  {voci.map((m) => (
                    <div key={m.id} className={s.row}>
                      <div className={s.body}>
                        <span className={m.fields.Attivo === false ? s.off : undefined}>{m.fields.Title}</span>
                        <div className={s.badges}>
                          {m.fields.PerComponente && (
                            <Badge size="small" appearance="tint" color="informative">
                              uno per componente
                            </Badge>
                          )}
                          {m.fields.SoloRTI && (
                            <Badge size="small" appearance="tint" color="warning">
                              solo RTI
                            </Badge>
                          )}
                          {m.fields.Predefinito === false && (
                            <Badge size="small" appearance="outline" color="subtle">
                              non proposto
                            </Badge>
                          )}
                          {m.fields.Attivo === false && (
                            <Badge size="small" appearance="outline" color="subtle">
                              non attivo
                            </Badge>
                          )}
                        </div>
                      </div>
                      <Button appearance="subtle" icon={<Edit20Regular />} aria-label={`Modifica ${m.fields.Title}`} onClick={() => setEdit({ voce: m })} />
                      <Button appearance="subtle" icon={<Delete20Regular />} aria-label={`Togli ${m.fields.Title}`} onClick={() => void elimina(m)} />
                    </div>
                  ))}
                </div>
              )}
            </Section>
          );
        })
      )}
      {edit && modello.data && (
        <VoceModelloDialog
          voce={edit.voce}
          tutte={modello.data}
          onClose={(saved) => {
            setEdit(undefined);
            if (saved) void modello.reload();
          }}
        />
      )}
    </Shell>
  );
}

function VoceModelloDialog(props: { voce?: VoceModello; tutte: VoceModello[]; onClose: (saved: boolean) => void }) {
  const s = useStyles();
  const data = useData();
  const f = props.voce?.fields ?? {};
  const [titolo, setTitolo] = useState(f.Title ?? "");
  const [plico, setPlico] = useState<Plico>(f.Plico ?? "Documentazione Amministrativa");
  const [ordine, setOrdine] = useState(f.Ordine != null ? String(f.Ordine) : "");
  const [perComponente, setPerComponente] = useState(!!f.PerComponente);
  const [soloRti, setSoloRti] = useState(!!f.SoloRTI);
  const [predefinito, setPredefinito] = useState(f.Predefinito !== false);
  const [attivo, setAttivo] = useState(f.Attivo !== false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string>();

  async function save() {
    const n = ordine.trim() ? Number(ordine) : undefined;
    if (n !== undefined && !Number.isInteger(n)) return setErr("L'ordine deve essere un numero intero.");
    setBusy(true);
    setErr(undefined);
    try {
      await salvaVoceModello(
        data,
        {
          Title: titolo,
          Plico: plico,
          Ordine: n ?? (props.voce ? undefined : ordineInCoda(props.tutte, plico)),
          PerComponente: perComponente,
          SoloRTI: soloRti,
          Predefinito: predefinito,
          Attivo: attivo,
        },
        props.voce,
      );
      props.onClose(true);
    } catch (e) {
      setErr(describeError(e).detail);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open onOpenChange={(_, d) => !d.open && props.onClose(false)}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{props.voce ? "Documento del modello" : "Nuovo documento"}</DialogTitle>
          <DialogContent className={s.form}>
            {err && (
              <MessageBar intent="error">
                <MessageBarBody>{err}</MessageBarBody>
              </MessageBar>
            )}
            <Field label="Documento" required>
              <Input value={titolo} onChange={(_, d) => setTitolo(d.value)} />
            </Field>
            <div className={s.two}>
              <Field label="Plico">
                <Select className={s.full} value={plico} onChange={(_, d) => setPlico(d.value as Plico)}>
                  {PLICHI.map((x) => (
                    <option key={x}>{x}</option>
                  ))}
                </Select>
              </Field>
              <Field label="Ordine" hint="Posizione nel plico">
                <Input inputMode="numeric" value={ordine} placeholder="in fondo" onChange={(_, d) => setOrdine(d.value)} />
              </Field>
            </div>
            <Switch label="Uno per ogni componente dell'RTI" checked={perComponente} onChange={(_, d) => setPerComponente(d.checked)} />
            <Switch label="Solo quando si partecipa in RTI" checked={soloRti} onChange={(_, d) => setSoloRti(d.checked)} />
            <Switch label="Già selezionato quando si prepara la checklist" checked={predefinito} onChange={(_, d) => setPredefinito(d.checked)} />
            <Switch label="Attivo (non attivo = non compare più)" checked={attivo} onChange={(_, d) => setAttivo(d.checked)} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => props.onClose(false)} disabled={busy}>
              Annulla
            </Button>
            <Button appearance="primary" onClick={save} disabled={busy}>
              {busy ? "Salvataggio…" : "Salva"}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
